import type { ReactNode } from "react";
import { useId } from "react";
import { useProfileEditorBrandAccent } from "../../Context/ProfileEditorBrandAccentContext";
import { staffFieldControlSlot, staffFieldRoot } from "./fieldStyles";

export type RadioGroupProps<T = unknown> = {
  title?: ReactNode;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltip?: string;
  className?: string;
  contentClassName?: string;
  name?: string;
  options: T[];
  value: string;
  onChange: (selected: string) => void;
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  orientation?: "vertical" | "horizontal";
  labelFirst?: boolean;
};

const contentLayout = (orientation: "vertical" | "horizontal") =>
  orientation === "horizontal"
    ? "flex flex-row flex-wrap gap-x-6 gap-y-2"
    : "flex flex-col gap-2";

export default function RadioGroup<T> ({
  title,
  required = false,
  error = false,
  errorMessage,
  tooltip,
  className = "",
  contentClassName = "",
  name,
  options,
  value,
  onChange,
  getOptionValue,
  getOptionLabel,
  getOptionDisabled,
  orientation = "vertical",
  labelFirst = false,
}: RadioGroupProps<T>) {
  const reactId = useId();
  const groupName = name ?? `radio-group${reactId.replace(/:/g, "")}`;
  const titleId = `${groupName}-title`;
  const brandAccent = useProfileEditorBrandAccent();

  const inputClass = `h-5 w-5 my-auto shrink-0 cursor-pointer ${brandAccent
      ? "text-primary accent-primary"
      : "text-secondary accent-secondary"
    } disabled:cursor-not-allowed disabled:opacity-50`;

  const showContentSlot = error || options.length > 0;

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title != null && title !== "" && (
        <div
          id={titleId}
          title={tooltip}
          className="shrink-0 font-semibold text-gray-font"
        >
          {title}
          {required && <span className="text-primary"> *</span>}
        </div>
      )}

      {showContentSlot && (
        <div className={staffFieldControlSlot}>
          <div
            role="radiogroup"
            aria-labelledby={
              title != null && title !== "" ? titleId : undefined
            }
            aria-invalid={error || undefined}
            aria-required={required || undefined}
            className={`px-1 py-0.5 ${contentLayout(orientation)} ${contentClassName} ${error
                ? "rounded-md outline outline-2 outline-primary-dark"
                : ""
              }`}
          >
            {options.map((option, index) => {
              const optionValue = getOptionValue(option);
              const key = optionValue || `option-${index}`;
              const disabled = getOptionDisabled?.(option) ?? false;
              const selected = value === optionValue;

              const bullet = (
                <input
                  type="radio"
                  name={groupName}
                  value={optionValue}
                  checked={selected}
                  disabled={disabled}
                  onChange={() => onChange(optionValue)}
                  className={inputClass}
                />
              );

              const text = (
                <span
                  className={`select-none text-md ${disabled ? "text-gray-400" : "text-gray-font"
                    } ${!disabled
                      ? brandAccent
                        ? "group-hover:text-primary"
                        : "group-hover:text-secondary"
                      : ""
                    }`}
                >
                  {getOptionLabel(option)}
                </span>
              );

              return (
                <label
                  key={key}
                  className={`group inline-flex max-w-full cursor-pointer items-start gap-2.5 ${disabled ? "cursor-not-allowed" : ""
                    }`}
                >
                  {labelFirst ? (
                    <>
                      {text}
                      {bullet}
                    </>
                  ) : (
                    <>
                      {bullet}
                      {text}
                    </>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {error && errorMessage && (
        <span className="shrink-0 text-sm text-primary">{errorMessage}</span>
      )}
    </div>
  );
}
